import React, { useRef, useState, useContext, useMemo } from 'react';
import { toast } from 'react-toastify';
import { DropdownItem } from 'reactstrap';
import { FaArrowRightToBracket } from 'react-icons/fa6';
import { APP_TOKEN } from 'constants';
import { authContext } from 'contexts/AuthContext';
import { collectPermissionNames, hasAnyPermission } from 'utils/permissions';

const GRAPHQL_ENDPOINT = process.env.REACT_APP_GRAPHQL_ENDPOINT;

const resolveTargetLoginUrl = (backendLoginUrl) => {
  if (!backendLoginUrl) return '';
  const vendorWebOverride = process.env.REACT_APP_VENDOR_WEB_URL;
  if (!vendorWebOverride) return backendLoginUrl;

  try {
    const originalUrl = new URL(backendLoginUrl);
    const overrideUrl = new URL(vendorWebOverride);
    originalUrl.protocol = overrideUrl.protocol;
    originalUrl.host = overrideUrl.host;
    return originalUrl.toString();
  } catch {
    return backendLoginUrl;
  }
};

const postGraphqlQuery = async (query, token, fallbackMessage) => {
  const response = await fetch(GRAPHQL_ENDPOINT, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify({ query }),
  });
  const result = await response.json().catch(() => null);

  if (!response.ok || result?.errors?.length) {
    const graphQLError = result?.errors
      ?.map(
        (err) =>
          err?.exception?.message ||
          (err?.message?.includes(' : ')
            ? err.message.split(' : ').pop()
            : err?.message),
      )
      .filter(Boolean)
      .join(', ');
    throw new Error(graphQLError || result?.message || fallbackMessage);
  }

  return result?.data;
};

const VendorLoginButton = ({ row }) => {
  const { auth } = useContext(authContext);
  const loginInProgress = useRef(false);
  const [loading, setLoading] = useState(false);

  const permissionNames = useMemo(
    () => collectPermissionNames(auth?.data),
    [auth?.data],
  );

  const hasVendorLoginPermission = useMemo(
    () => hasAnyPermission(permissionNames, ['ADMIN_LOGIN_AS_VENDOR']),
    [permissionNames],
  );

  const handleLogin = async (e) => {
    e?.stopPropagation?.();
    if (loading || loginInProgress.current) return;

    if (!hasVendorLoginPermission) {
      toast.error('You do not have permission to log in as vendor.');
      return;
    }

    if (!GRAPHQL_ENDPOINT) {
      toast.error('GraphQL API URL is not configured.');
      return;
    }

    const vendorId = Number(row?.original?.id || row?.id);
    if (!Number.isSafeInteger(vendorId) || vendorId <= 0) {
      toast.error('Unable to identify this vendor.');
      return;
    }

    let adminToken;
    try {
      adminToken = JSON.parse(
        window.localStorage.getItem(APP_TOKEN) || 'null',
      )?.token;
    } catch {
      toast.error('Unable to read the admin session. Please sign in again.');
      return;
    }

    if (!adminToken) {
      toast.error('Admin session has expired. Please sign in again.');
      return;
    }

    // Step 1: Open blank window synchronously on user click to prevent popup blocking
    const w = window.open('about:blank', '_blank');
    if (!w) {
      toast.error('Please allow popups to open the vendor portal.');
      return;
    }

    try {
      w.document.write(`
        <!DOCTYPE html>
        <html>
          <head><title>Opening Vendor Portal...</title></head>
          <body style="font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; display: flex; align-items: center; justify-content: center; height: 100vh; margin: 0; background: #f9fafb;">
            <div style="text-align: center; padding: 20px;">
              <h2 style="color: #111827; margin-bottom: 8px;">Opening Vendor Session...</h2>
              <p style="color: #6b7280; font-size: 14px; margin: 0;">Please wait, redirecting to vendor marketplace...</p>
            </div>
          </body>
        </html>
      `);
      w.document.close();
    } catch {
      // Ignore if document access is restricted
    }

    loginInProgress.current = true;
    setLoading(true);

    try {
      // Step 2: Call Generate API - backend returns { code, expiresAt, loginUrl }
      const generateQuery = `mutation { generateVendorAutoLoginCode(vendorId: ${vendorId}) { code expiresAt loginUrl } }`;
      const data = await postGraphqlQuery(
        generateQuery,
        adminToken,
        'Unable to generate vendor login code.',
      );

      // Take loginUrl directly from backend
      const rawLoginUrl = data?.generateVendorAutoLoginCode?.loginUrl;
      if (!rawLoginUrl) {
        throw new Error('Login URL was not returned by the server.');
      }

      const targetLoginUrl = resolveTargetLoginUrl(rawLoginUrl);

      // Step 3: Redirect popup to vendor login handoff page
      w.location.replace(targetLoginUrl);
    } catch (error) {
      if (w && !w.closed) {
        w.close();
      }
      toast.error(error?.message || 'Unable to sign in as vendor.');
    } finally {
      loginInProgress.current = false;
      setLoading(false);
    }
  };

  if (!hasVendorLoginPermission) {
    return null;
  }

  return (
    <DropdownItem onClick={handleLogin} disabled={loading}>
      <FaArrowRightToBracket /> {loading ? 'Logging in...' : 'Login'}
    </DropdownItem>
  );
};

export default VendorLoginButton;
